import _ from 'lodash';
import tinycolor, {ColorInputWithoutInstance, ColorFormats} from 'tinycolor2';
import colordiff from 'color-diff';
import {isNumeric} from '@packages/systems/core/utils/JsUtils';
import {collapseSpaces} from '@packages/systems/core/utils/TextUtils';
import {ColorConfig} from '@packages/systems/core/constants/ColorConfig';
import {memoize} from '@packages/utilities/memo';

export const TRANSPARENT = 'transparent';

export type HSL = {
  h: number;
  s: number;
  l: number;
};

export type HSLA = {
  h: number;
  s: number;
  l: number;
  a: number;
};

export type HSV = {
  h: number;
  s: number;
  v: number;
};

export type HSVA = {
  h: number;
  s: number;
  v: number;
  a: number;
};

/**
 * Shortens RGB to HEX6/3 or a named color.
 * @param  {String} rgb   An RGB color, e.g. rgb(255, 0, 0)
 * @return {String}       A more human-readable version of the color.
 */
export const humanizeRgb = (rgb: string): string => {
  const color = tinycolor(rgb);
  const alpha = color.getAlpha();

  if (alpha === 1) {
    return hexOrName(color.toHexString());
  } else if (alpha === 0 && color.toString('rgb') === 'rgba(0, 0, 0, 0)') {
    return 'transparent';
  } else {
    return color.toString();
  }
};

export const colorToHSL = (color: ColorInputWithoutInstance) => {
  if (typeof color === 'string') {
    if (color.indexOf('hsla') === 0) {
      const hslaMatch = color.match(
        /hsla\(([\d\.]+),\s*([\d\.]+)%,\s*([\d\.]+)%,\s*([\d\.]+)\)/
      );
      if (hslaMatch) {
        const [, h, s, l, a] = hslaMatch;

        return {
          h: Number(h),
          s: Number(s) / 100,
          l: Number(l) / 100,
          a: Number(a),
        };
      }
    }
  }

  return tinycolor(color).toHsl() as HSLA;
};

export const colorToHSV = (color: ColorInputWithoutInstance) =>
  hslToHsv(colorToHSL(color));

/**
 * Tries to convert a HEX6 color to either a named color or a HEX3 variation.
 * @param  {String} hex6   A HEX color e.g. #FF0000
 * @return {String}        A named version e.g. red (only if there is a match), a HEX3 version e.g. #F00 (only if appropriate), or the original HEX6 value.
 */
export const hexOrName = (hex6: string): string => {
  let color = _hexToName(hex6);
  const isHex = color && color.length === 7 && /^#[0-9a-fA-F]{6}$/.test(color);

  // If still hex, see if we can convert to hex3
  if (
    isHex &&
    // @ts-expect-error - TS18048 - 'color' is possibly 'undefined'. | TS18048 - 'color' is possibly 'undefined'.
    color[1] === color[2] &&
    // @ts-expect-error - TS18048 - 'color' is possibly 'undefined'. | TS18048 - 'color' is possibly 'undefined'.
    color[3] === color[4] &&
    // @ts-expect-error - TS18048 - 'color' is possibly 'undefined'. | TS18048 - 'color' is possibly 'undefined'.
    color[5] === color[6]
  ) {
    // @ts-expect-error - TS18048 - 'color' is possibly 'undefined'. | TS18048 - 'color' is possibly 'undefined'. | TS18048 - 'color' is possibly 'undefined'.
    color = '#' + color[1] + color[3] + color[5];
  }

  // @ts-expect-error - TS2322 - Type 'string | undefined' is not assignable to type 'string'.
  return color;
};

/**
 * Converts hex values without a pound to valid format.
 * @param  {String} colorStr An arbitrary color string
 * @return {String}          A valid color string
 */
export const parseColor = (
  colorStr: ColorInputWithoutInstance
): string | null => {
  const color = tinycolor(fixRgba(colorStr));
  if (!color.isValid()) {
    return null;
  }
  return humanizeRgb(color.toString('rgb'));
};

/**
 * Checks if an arbitrary string can be converted to a color.
 * @param  {String} colorStr An arbitrary color string
 * @return {Boolean}         True if the string is a color string
 */
export const isValidColor = (colorStr: string) => tinycolor(colorStr).isValid();

export const isTransparent = (colorString: string) =>
  tinycolor(colorString).getAlpha() === 0;

export const isPartiallyTransparent = (colorString: string): boolean => {
  const alpha = tinycolor(colorString).getAlpha();

  return alpha < 1 && alpha > 0;
};

export const getHex = (color: ColorInputWithoutInstance) =>
  tinycolor(color).toString('hex');

export const fromHue = (hue: number) => fromHsv(hue, 100, 100);

export const getHSBA = (
  colorStr: string
): {
  hue: number;
  saturation: number;
  brightness: number;
  opacity: number;
} => {
  const hsva = getHSVA(colorStr);

  return {
    hue: hsva.h,
    saturation: hsva.s * 100,
    brightness: hsva.v * 100,
    opacity: hsva.a,
  };
};

export const hsvToHsl = ({h, s, v, a}: HSVA): HSLA => {
  const h2 = (2 - s) * v;

  // Default to S for division since we want to maintain
  // that value if V is 0. If h2 is 0 (happens when v is 0 again), then default
  // to 1 to prevent Infinity case.
  const s2 = (s * v || s) / ((h2 < 1 ? h2 : 2 - h2) || 1);

  return {
    h,
    s: s2,
    l: h2 / 2,
    a,
  };
};

export const hslToHsv = ({h, s, l, a}: HSLA): HSVA => {
  const s2 = s * (l < 0.5 ? l : 1 - l);

  const v = l + s2;

  return {
    h,
    s: l ? (2 * s2) / v : s,
    v,
    a,
  };
};

export const hslToString = ({h, s, l, a}: HSLA) => {
  return `hsla(${h}, ${(s * 100).toFixed(2)}%, ${(l * 100).toFixed(
    2
  )}%, ${a.toFixed(2)})`;
};

export const hsvToString = (hsva: HSVA) => hslToString(hsvToHsl(hsva));

export const getHSVA = (colorStr: string) => colorToHSV(colorStr);

export const fromHsva = (
  hue: number,
  saturation: number,
  brightness: number,
  opacity: number
) =>
  tinycolor({h: hue, s: saturation, v: brightness, a: opacity}).toString('rgb');

export const fromHsv = (hue: number, saturation: number, brightness: number) =>
  fromHsva(hue, saturation, brightness, 1);

export const getOpacity = (colorStr: string) => tinycolor(colorStr).getAlpha();

export const getHue = (colorStr: string) => tinycolor(colorStr).toHsv().h;

/**
 * Converts incomplete/incorrect RGB or RGBa values to proper ones.
 * @param  {String} colorStr  A color value, e.g. "rgba(255,255,255)"
 * @return {String}           A cleaned up value, e.g. "rgb(255, 255, 255)"
 */
export const fixRgba = (colorStr: ColorInputWithoutInstance) => {
  if (colorStr && typeof colorStr === 'string') {
    const clean = _.trim(colorStr.toLowerCase());
    if (!/^rgba?\([^)]+\)$/.test(clean)) {
      return colorStr;
    }

    const components = clean
      .split(/[,()]/)
      .map((s) => _.trim(s))
      .filter(isNumeric);
    if (components.length < 3) {
      return colorStr;
    }

    components[3] = components[3] || '1';

    const alphaValue = parseFloat(components[3]);
    const needsAlpha = !isNaN(alphaValue) && alphaValue < 1.0;

    if (!needsAlpha) {
      components.pop();
    }

    const color =
      (needsAlpha ? 'rgba' : 'rgb') + '(' + components.join(', ') + ')';
    return color;
  }

  return colorStr;
};

/**
 * Converts a named color to a human-readable name
 * @param  {String} namedColor An HTML color, e.g. darkslategray
 * @return {String}            A cleaned name, e.g. 'Dark Slate Gray'
 */
export const humanizeNamedColor = (namedColor: string): string => {
  let cleanColor = namedColor;
  const postfixes = [
    'blue',
    'green',
    'yellow',
    'red',
    'white',
    'gray',
    'grey',
    'pink',
    'almond',
    'lavender',
    'lemon',
    'mint',
    'olive',
    'papaya',
    'peach',
    'brown',
  ];
  const prefixes = [
    'dark',
    'light',
    'medium',
    'deep',
    'misty',
    'old',
    'pale',
  ].concat(postfixes);

  for (let i = 0, len = prefixes.length; i < len; i++) {
    const prefix = prefixes[i];
    // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    if (cleanColor.indexOf(prefix) === 0) {
      // @ts-expect-error - TS2769 - No overload matches this call.
      cleanColor = cleanColor.replace(prefix, prefix + ' ');
      break;
    }
  }

  for (let i = 0, len = postfixes.length; i < len; i++) {
    const postfix = postfixes[i];
    if (_.endsWith(cleanColor, postfix)) {
      // @ts-expect-error - TS2769 - No overload matches this call.
      cleanColor = cleanColor.replace(postfix, ' ' + postfix);
      break;
    }
  }

  return _.trim(_.startCase(collapseSpaces(cleanColor)));
};

/**
 * Mixes two color values together to get some value in the middle.
 * @param  {String} color1  The first color.
 * @param  {String} color2  The second color.
 * @param  {Number} amount  A value between 0 and 100, with 0 being fully color1 and 100 being fully color2.
 * @return {String}         The CSS-compatible string representation of the blended color.
 */
export const mixTwoColors = (color1: string, color2: string, amount: number) =>
  tinycolor.mix(color1, color2, amount).toString();

// Some email clients will render the incorrect color cause they do not accept non-hex values.
// This util forces a HEX value.
export const getMixedColorOnWhite = (colorStr: string) => {
  const color = tinycolor(colorStr);
  const alphaPercent = color.getAlpha() * 100;
  const hexStr = color.toHexString();
  return tinycolor.mix('#FFFFFF', hexStr, alphaPercent).toHexString();
};

const transition = (a: number, b: number, amount: number): number =>
  a + (b - a) * amount;

export const transitionColor = (
  color1: ColorInputWithoutInstance,
  color2: ColorInputWithoutInstance,
  amount: number
) => {
  const rgba1 = tinycolor(color1).toRgb();
  const rgba2 = tinycolor(color2).toRgb();
  return tinycolor({
    r: transition(rgba1.r, rgba2.r, amount),
    g: transition(rgba1.g, rgba2.g, amount),
    b: transition(rgba1.b, rgba2.b, amount),
    a: transition(rgba1.a, rgba2.a, amount),
  }).toString();
};

/**
 * Finds the closest named CSS color to a specific color value.
 * @param  {String} colorStr Any color string.
 * @return {String}          The closest named CSS color to that color.
 */
export const closestColorName = (colorStr: string) => {
  const color = tinycolor(colorStr).toRgb();
  const originalRGB = {R: color.r, G: color.g, B: color.b} as const;
  const closestNamed = colordiff.closest(originalRGB, NAMED_COLOR_PALETTE);
  const namedColor = tinycolor({
    r: closestNamed.R,
    g: closestNamed.G,
    b: closestNamed.B,
  });
  return namedColor.toString('name');
};

export const mergeOpacity = (
  oldValue: ColorInputWithoutInstance,
  newValue: number
) => tinycolor(oldValue).setAlpha(newValue).toString();

export const toRgb = (color: ColorInputWithoutInstance) =>
  tinycolor(color).toRgb();
export const toRgbString = (color: ColorInputWithoutInstance) =>
  tinycolor(color).setAlpha(1).toRgbString();
export const createRgbaColor = toRgb;

export const rgba = (color: ColorInputWithoutInstance, alpha: number) =>
  tinycolor(color).setAlpha(alpha).toRgbString();

export const lighten =
  (amount?: number) =>
  (color: ColorInputWithoutInstance): string =>
    tinycolor(color).lighten(amount).toString();

export const darken =
  (amount?: number) =>
  (color: ColorInputWithoutInstance): string =>
    tinycolor(color).darken(amount).toString();

const NAME_MAP: {
  [hex: string]: string;
} = _.chain(ColorConfig)
  .map((row: {hex: string; name: string}) => [row.hex, row.name])
  .fromPairs()
  .value();

const NAMED_COLOR_PALETTE = ColorConfig.map(
  (namedColor: {hex: string; name: string}) => {
    const color = tinycolor(namedColor.hex).toRgb();
    return {R: color.r, G: color.g, B: color.b};
  }
);

/**
 * Converts a HEX color to a named color.
 * @param  {String} hex The HEX6 color.
 * @return {String}     A matching named color, or the original hex if there is no match.
 */
function _hexToName(hex: string) {
  if (!hex) {
    return hex;
  }

  // We have a match
  if (_.has(NAME_MAP, hex.toLowerCase())) {
    return NAME_MAP[hex.toLowerCase()];
  }

  // No corresponding name, just return hex
  return hex;
}

const getAbsoluteLuminanceDifference = memoize(
  (color1: tinycolor.ColorInput, color2: tinycolor.ColorInput) =>
    Math.abs(
      tinycolor(color1).getLuminance() - tinycolor(color2).getLuminance()
    )
);

export const getColorsWithHighestContrast = ({
  foregrounds,
  backgrounds,
}: {
  foregrounds: ColorInputWithoutInstance[];
  backgrounds: ColorInputWithoutInstance[];
}) => {
  const result = foregrounds.reduce<{
    foreground: ColorInputWithoutInstance | null;
    background: ColorInputWithoutInstance | null;
    difference: number;
  }>(
    (_colorsWithHighestContrast, foreground) => {
      return backgrounds.reduce((colorsWithHighestContrast, background) => {
        const {difference: maxDifference} = colorsWithHighestContrast;
        const difference = getAbsoluteLuminanceDifference(
          foreground,
          background
        );
        if (difference > maxDifference) {
          return {
            foreground,
            background,
            difference,
          };
        } else {
          return colorsWithHighestContrast;
        }
      }, _colorsWithHighestContrast);
    },
    {
      foreground: null,
      background: null,
      difference: -1,
    }
  );

  const {foreground, background} = result;
  return {foreground, background};
};

const applyAlpha = (
  color: ColorInputWithoutInstance,
  overColor: ColorInputWithoutInstance
): string => {
  const colorRGB = tinycolor(color).toRgb();
  const alpha = colorRGB.a;
  const overColorRGB = tinycolor(overColor).toRgb();
  return tinycolor
    .fromRatio(
      ['r', 'g', 'b'].reduce<ColorFormats.RGB>((acc, keyString) => {
        const key = keyString as keyof ColorFormats.RGB;
        acc[key] = colorRGB[key] * alpha + overColorRGB[key] * (1 - alpha);
        return acc;
      }, {} as ColorFormats.RGB)
    )
    .toString();
};

export const getContrast = ({
  bgColor,
  textColor = 'rgba(0,0,0,.7)',
  overColor = 'white',
}: {
  bgColor: string;
  textColor?: string;
  overColor?: string;
}): null | number => {
  if (tinycolor(bgColor).isValid()) {
    const solidBackgroundColor = applyAlpha(bgColor, overColor);
    const solidTextColor = applyAlpha(textColor, solidBackgroundColor);

    return tinycolor.readability(solidBackgroundColor, solidTextColor);
  }

  return null;
};

/* brazenly stolen from Chrome DevTools
https://github.com/ChromeDevTools/devtools-frontend/blob/090e3d5a3680376e1f743ea572872b5d506a3989/front_end/common/Color.js
/*
 * Copyright (C) 2009 Apple Inc.  All rights reserved.
 * Copyright (C) 2009 Joseph Pecoraro
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions
 * are met:
 *
 * 1.  Redistributions of source code must retain the above copyright
 *     notice, this list of conditions and the following disclaimer.
 * 2.  Redistributions in binary form must reproduce the above copyright
 *     notice, this list of conditions and the following disclaimer in the
 *     documentation and/or other materials provided with the distribution.
 * 3.  Neither the name of Apple Computer, Inc. ("Apple") nor the names of
 *     its contributors may be used to endorse or promote products derived
 *     from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY APPLE AND ITS CONTRIBUTORS "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL APPLE OR ITS CONTRIBUTORS BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
const _contrastThresholds = {
  large: {
    aa: 3.0,
    aaa: 4.5,
  },
  small: {
    aa: 4.5,
    aaa: 7.0,
  },
} as const;
function _blendColors(
  fgRGBA: Array<number> | Array<number>,
  bgRGBA: Array<number>,
  out_blended: Array<number>
) {
  const alpha = fgRGBA[3];
  // @ts-expect-error - TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'. | TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
  out_blended[0] = (1 - alpha) * bgRGBA[0] + alpha * fgRGBA[0];
  // @ts-expect-error - TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'. | TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
  out_blended[1] = (1 - alpha) * bgRGBA[1] + alpha * fgRGBA[1];
  // @ts-expect-error - TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'. | TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'.
  out_blended[2] = (1 - alpha) * bgRGBA[2] + alpha * fgRGBA[2];
  // @ts-expect-error - TS18048 - 'alpha' is possibly 'undefined'. | TS2532 - Object is possibly 'undefined'. | TS18048 - 'alpha' is possibly 'undefined'.
  out_blended[3] = alpha + bgRGBA[3] * (1 - alpha);
}
function _luminance(_rgba: Array<number> | Array<number>) {
  const rSRGB = _rgba[0];
  const gSRGB = _rgba[1];
  const bSRGB = _rgba[2];
  const r =
    // @ts-expect-error - TS18048 - 'rSRGB' is possibly 'undefined'. | TS18048 - 'rSRGB' is possibly 'undefined'. | TS18048 - 'rSRGB' is possibly 'undefined'.
    rSRGB <= 0.03928 ? rSRGB / 12.92 : Math.pow((rSRGB + 0.055) / 1.055, 2.4);
  const g =
    // @ts-expect-error - TS18048 - 'gSRGB' is possibly 'undefined'. | TS18048 - 'gSRGB' is possibly 'undefined'. | TS18048 - 'gSRGB' is possibly 'undefined'.
    gSRGB <= 0.03928 ? gSRGB / 12.92 : Math.pow((gSRGB + 0.055) / 1.055, 2.4);
  const b =
    // @ts-expect-error - TS18048 - 'bSRGB' is possibly 'undefined'. | TS18048 - 'bSRGB' is possibly 'undefined'. | TS18048 - 'bSRGB' is possibly 'undefined'.
    bSRGB <= 0.03928 ? bSRGB / 12.92 : Math.pow((bSRGB + 0.055) / 1.055, 2.4);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function _hsva2hsla(hsva: Array<number>, out_hsla: Array<number>) {
  const h = hsva[0];
  let s = hsva[1];
  const v = hsva[2];
  // @ts-expect-error - TS18048 - 's' is possibly 'undefined'. | TS18048 - 'v' is possibly 'undefined'.
  const t = (2 - s) * v;
  if (v === 0 || s === 0) {
    s = 0;
  } else {
    // @ts-expect-error - TS18048 - 's' is possibly 'undefined'. | TS18048 - 'v' is possibly 'undefined'.
    s *= v / (t < 1 ? t : 2 - t);
  }
  // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
  out_hsla[0] = h;
  // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
  out_hsla[1] = s;
  out_hsla[2] = t / 2;
  // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
  out_hsla[3] = hsva[3];
}
function _hsl2rgb(hsl: Array<number>, out_rgb: Array<number>) {
  const h = hsl[0];
  let s = hsl[1];
  const l = hsl[2];
  function hue2rgb(p: number, q: number, _h: number) {
    if (_h < 0) {
      _h += 1;
    } else if (_h > 1) {
      _h -= 1;
    }
    if (_h * 6 < 1) {
      return p + (q - p) * _h * 6;
    }
    if (_h * 2 < 1) {
      return q;
    }
    if (_h * 3 < 2) {
      return p + (q - p) * (2 / 3 - _h) * 6;
    }
    return p;
  }
  // @ts-expect-error - TS18048 - 's' is possibly 'undefined'.
  if (s < 0) {
    s = 0;
  }
  let q;
  // @ts-expect-error - TS18048 - 'l' is possibly 'undefined'.
  if (l <= 0.5) {
    // @ts-expect-error - TS18048 - 'l' is possibly 'undefined'. | TS18048 - 's' is possibly 'undefined'.
    q = l * (1 + s);
  } else {
    // @ts-expect-error - TS18048 - 'l' is possibly 'undefined'. | TS18048 - 's' is possibly 'undefined'. | TS18048 - 'l' is possibly 'undefined'. | TS18048 - 's' is possibly 'undefined'.
    q = l + s - l * s;
  }
  // @ts-expect-error - TS18048 - 'l' is possibly 'undefined'.
  const p = 2 * l - q;
  // @ts-expect-error - TS18048 - 'h' is possibly 'undefined'.
  const tr = h + 1 / 3;
  const tg = h;
  // @ts-expect-error - TS18048 - 'h' is possibly 'undefined'.
  const tb = h - 1 / 3;
  out_rgb[0] = hue2rgb(p, q, tr);
  // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
  out_rgb[1] = hue2rgb(p, q, tg);
  out_rgb[2] = hue2rgb(p, q, tb);
  // @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
  out_rgb[3] = hsl[3];
}
function _hsva2rgba(hsva: Array<number>, out_rgba: Array<number>) {
  const _tmpHSLA = [0, 0, 0, 0];
  _hsva2hsla(hsva, _tmpHSLA);
  _hsl2rgb(_tmpHSLA, out_rgba);
}
export function generateContrastRatioLinePath(
  foregroundColor: {
    h: number;
    s: number;
    v: number;
    a: number;
  },
  backgroundColor: ColorInputWithoutInstance,
  width: number,
  height: number,
  fontSize: 'small' | 'large',
  level: 'aa' | 'aaa'
) {
  const requiredContrast = _contrastThresholds[fontSize][level];
  if (!width || !height || !requiredContrast) {
    return null;
  }
  const dS = 0.02;
  const epsilon = 0.0002;
  const H = 0;
  const S = 1;
  const V = 2;
  const A = 3;
  const fg = tinycolor(foregroundColor);
  const bg = tinycolor(backgroundColor);
  const {r, g, b, a} = fg.toRgb();
  const fgRGBA = [r / 255, g / 255, b / 255, a];
  const {h: fgH, s: fgS, v: fgV, a: fgA} = foregroundColor;
  const fgHSVA = [fgH / 360, fgS, fgV, fgA];
  const {r: bgR, g: bgG, b: bgB, a: bgA} = bg.toRgb();
  const bgRGBA = [bgR / 255, bgG / 255, bgB / 255, bgA];
  const bgLuminance = _luminance(bgRGBA);
  const blendedRGBA: Array<number> = [];
  _blendColors(fgRGBA, bgRGBA, blendedRGBA);
  const fgLuminance = _luminance(blendedRGBA);
  const fgIsLighter = fgLuminance > bgLuminance;
  const desiredLuminance = (function desiredLuminance(lum, contrast, lighter) {
    function computeLuminance() {
      if (lighter) {
        return (lum + 0.05) * contrast - 0.05;
      }
      return (lum + 0.05) / contrast - 0.05;
    }
    let dL = computeLuminance();
    if (dL < 0 || dL > 1) {
      lighter = !lighter;
      dL = computeLuminance();
    }
    return dL;
  })(bgLuminance, requiredContrast, fgIsLighter);
  let lastV = fgHSVA[V];
  let currentSlope = 0;
  const candidateHSVA = [fgHSVA[H], 0, 0, fgHSVA[A]];
  let pathBuilder: Array<string> = [];
  const candidateRGBA: Array<number> = [];
  // @ts-expect-error - TS2345 - Argument of type '(number | undefined)[]' is not assignable to parameter of type 'number[]'.
  _hsva2rgba(candidateHSVA, candidateRGBA);
  _blendColors(candidateRGBA, bgRGBA, blendedRGBA);
  function updateCandidateAndComputeDelta(index: number, x: number) {
    candidateHSVA[index] = x;
    // @ts-expect-error - TS2345 - Argument of type '(number | undefined)[]' is not assignable to parameter of type 'number[]'.
    _hsva2rgba(candidateHSVA, candidateRGBA);
    _blendColors(candidateRGBA, bgRGBA, blendedRGBA);
    return _luminance(blendedRGBA) - desiredLuminance;
  }
  function approach(index: number) {
    let x = candidateHSVA[index];
    let multiplier = 1;
    // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
    let dLuminance = updateCandidateAndComputeDelta(index, x);
    let previousSign = Math.sign(dLuminance);
    for (let guard = 100; guard; guard--) {
      if (Math.abs(dLuminance) < epsilon) {
        return x;
      }
      const sign = Math.sign(dLuminance);
      if (sign !== previousSign) {
        multiplier /= 2;
        previousSign = sign;
        // @ts-expect-error - TS18048 - 'x' is possibly 'undefined'. | TS18048 - 'x' is possibly 'undefined'.
      } else if (x < 0 || x > 1) {
        return null;
      }
      // @ts-expect-error - TS18048 - 'x' is possibly 'undefined'.
      x += multiplier * (index === V ? -dLuminance : dLuminance);
      // @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'number'.
      dLuminance = updateCandidateAndComputeDelta(index, x);
    }
    return null;
  }
  let s;
  for (s = 0; s < 1 + dS; s += dS) {
    s = Math.min(1, s);
    candidateHSVA[S] = s;
    // @ts-expect-error - TS18048 - 'lastV' is possibly 'undefined'.
    candidateHSVA[V] = lastV + currentSlope * dS;
    const v = approach(V);
    if (v === null) {
      break;
    }
    // @ts-expect-error - TS18048 - 'v' is possibly 'undefined'. | TS18048 - 'lastV' is possibly 'undefined'.
    currentSlope = s === 0 ? 0 : (v - lastV) / dS;
    lastV = v;
    pathBuilder.push(pathBuilder.length ? 'L' : 'M');
    pathBuilder.push((s * width).toFixed(2));
    // @ts-expect-error - TS18048 - 'v' is possibly 'undefined'.
    pathBuilder.push(((1 - v) * height).toFixed(2));
  }
  if (s != null && s < 1 + dS) {
    s -= dS;
    candidateHSVA[V] = 1;
    s = approach(S);
    if (s !== null) {
      // @ts-expect-error - TS18048 - 's' is possibly 'undefined'.
      pathBuilder = pathBuilder.concat(['L', (s * width).toFixed(2), '-0.1']);
    }
  }
  if (pathBuilder.length === 0) {
    return null;
  }
  return pathBuilder.join(' ');
}

// from https://stackoverflow.com/a/11615135
export const blendColors = (
  foreground: ColorInputWithoutInstance,
  background: ColorInputWithoutInstance
): string => {
  const {
    r: fgRed,
    g: fgGreen,
    b: fgBlue,
    a: fgAlpha,
  } = tinycolor(foreground).toRgb();
  const {
    r: bgRed,
    g: bgGreen,
    b: bgBlue,
    a: bgAlpha,
  } = tinycolor(background).toRgb();
  const r = Math.round(
    (fgAlpha * (fgRed / 255) + (1 - fgAlpha) * (bgRed / 255)) * 255
  );
  const g = Math.round(
    (fgAlpha * (fgGreen / 255) + (1 - fgAlpha) * (bgGreen / 255)) * 255
  );
  const b = Math.round(
    (fgAlpha * (fgBlue / 255) + (1 - fgAlpha) * (bgBlue / 255)) * 255
  );

  return tinycolor({r, g, b})
    .setAlpha(fgAlpha + bgAlpha * (1 - fgAlpha))
    .toRgbString();
};
